import { HTMLAttributes, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@components/common/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { DictType } from "@pma/types";
import { RESET_PASSWORD_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes";
import { cn } from "@pma/utils/helpers";
import { toast } from "@hooks/use-toast";
import { Card } from "@components/common/card";
import { Link, useNavigate } from "react-router-dom";
import {
  LOGIN_COMPLETE_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE,
} from "@pma/utils/page-routes";
import Recaptcha from "./recaptcha";
import useTranslator from "@hooks/use-translator";
import { PasswordInput } from "@components/common/password-input";
import { setCookie } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";

interface ResetPasswordProps extends HTMLAttributes<HTMLDivElement> {
  inviteKey: string;
}
// let regex =/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(!=.*[@.#$!%*?&])[A-Za-z\d@.#$!%*?&]{8,15}$/;
let regex =/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@.#$!%*?&]{8,}$/;
const formSchema = z
  .object({
    password: z
      .string()
      .min(1, {
        message: "Please enter your password",
      })
      .min(8, {
        message: "Password must be at least 8 characters long",
      })
      // .max(8, {
      //   message: "Password must be not more than 8 characters long",
      // })
      .regex(regex,{message:'Please include at least one uppercase, one lowercase and one digit.'})
      ,
    confirm_password: z.string()
    // .min(8, {
    //   message: "Please confirm your password",
    // }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match.",
    path: ["confirm_password"],
  });

function ResetPasswordForm({
  inviteKey,
  className,
  ...props
}: ResetPasswordProps) {
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  });
  const [submittedData, setSubmittedData] = useState({});
  const [v2Fallback, setV2Fallback] = useState(false);

  const { translate } = useTranslator();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  const resetPassword = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Failed to complete reCAPTCHA"),
        variant: "info",
      });
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return;
    }

    const response = await axios.post(
      SERVER_URL + RESET_PASSWORD_ENDPOINT,
      {
        key_value: inviteKey,
        password: payload?.password,
        recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
        recaptcha_response_v2: payload?.v2Token,
      },
      {},
    );

    return response.data;
  };

  const resetPasswordMutation = useMutation({
    mutationKey: ["reset-password"],
    mutationFn: (payload: DictType) => resetPassword(payload),
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: `${translate("Password Reset")} ${translate("Proceed to login")}`,
        variant: "success",
      });

      // persist in cookieStorage
      setCookie(null, ACCESS_TOKEN, data, {
        maxAge: 24 * 60 * 60 * 30,
        sameSite: "strict",
        path: "/",
      });

      navigate(LOGIN_COMPLETE_PAGE_ROUTE);
    },
    onError: (error: DictType) => {
      
      const errorMessage = error?.response?.data?.message;
      
        if (errorMessage.toLowerCase() === "invalid captcha response" || errorMessage==='Invalid Captcha Response. Please try again.') {
          setV2Fallback(true);
        }
      
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to update password"),
        variant: "error",
      });
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    // Track for v2 resubmission
    setSubmittedData(data);
    resetPasswordMutation.mutate(data);
  }

  const reSubmit = (token: string) => {
    resetPasswordMutation.mutate({ ...submittedData, v2Token: token });
  };

  return (
    <Card
      className="h-[450px] w-full rounded-md border-b-0 border-none p-6 md:w-[350px]"
      style={{
        background: "linear-gradient(blue 5.01%, rgb(232, 203, 203, 0%)",
        borderRadius: "15px 15px 0px 0px",
      }}
    >
      <div className="flex flex-col items-center justify-center space-y-2 text-left">
        <div className="relative mt-0 p-0">
          <img
            src={`${process.env.PUBLIC_URL}/group-232.svg`}
            alt="Brand logo"
          />
        </div>

        <div className="my-2 flex flex-col space-y-2 text-left">
          <h1 className="text-md font-semibold capitalize tracking-tight text-white">
            {translate("reset password")}{" "}
          </h1>
        </div>
      </div>
      <div className={cn("grid gap-6 text-white", className)} {...props}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-2 text-left">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col space-y-2">
                    <FormLabel className="w-full capitalize ltr:text-left rtl:text-right">
                      {translate("Password")}
                    </FormLabel>

                    <FormControl className="">
                      <PasswordInput
                        placeholder="********"
                        {...field}
                        className="w-full rounded-full bg-white text-black"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col space-y-2">
                    <FormLabel className="w-full capitalize ltr:text-left rtl:text-right">
                      {translate("Confirm Password")}
                    </FormLabel>

                    <FormControl className="">
                      <PasswordInput
                        placeholder="********"
                        {...field}
                        className="w-full rounded-full bg-white text-black"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                className="mt-2 rounded-full"
                loading={resetPasswordMutation.isPending}
                disabled={resetPasswordMutation.isPending}
              >
                {translate("Reset password")}{" "}
              </Button>
            </div>
          </form>
        </Form>

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-foreground">
              {translate("or")}{" "}
            </span>
          </div>
        </div>
        <Button className="rounded-full bg-accent text-primary hover:bg-secondary/40 hover:text-white">
          <Link to={LOGIN_PAGE_ROUTE}>{translate("Back to Login")}</Link>
        </Button>
      </div>

      {/* GOOGLE Recaptcha */}
      <Recaptcha
        setToken={setRecaptchaToken}
        v2={v2Fallback}
        reSubmit={reSubmit}
      />
    </Card>
  );
}

export default ResetPasswordForm;
